/**
 * Chat-runtime ("Copilot") detection — single source of truth for the
 * frontend.
 *
 * A "Copilot" is a Lambda-hosted CHAT agent (Slack/Lark-style, one message =
 * one chat turn) rather than a normal Fargate workflow. The two chat templates
 * are chat-ops-bot and zibby-copilot; forward-marked templates also carry
 * `runtime: 'chat'` on the marketplace row.
 *
 * Detection (OR):
 *   1. The marketplace row / overlay / pipeline carries `runtime === 'chat'`
 *      (forward marker, set on zibby-copilot from newer template versions).
 *   2. The marketplace slug is in CHAT_RUNTIME_SLUGS (fallback for rows that
 *      predate the runtime flag).
 *
 * Mirrors the backend services/account-copilots.js isCopilotTemplate /
 * slack-chat-mapping.js chatTemplateSlugs so the UX gate and the hard guard
 * agree on what counts as a Copilot.
 */

// Slug allow-list for chat/Lambda agents. Kept here so detection is robust even
// when the marketplace `runtime` field hasn't propagated onto a row yet.
export const CHAT_RUNTIME_SLUGS = new Set(['chat-ops-bot', 'zibby-copilot']);

/**
 * True when `pipeline` is a Copilot (chat-runtime). `pipeline` may be a deployed
 * workflow row (carries `runtime` and/or `marketplaceSlug`), a marketplace entry
 * (carries `slug` and/or `runtime`), or anything in between — we read every
 * shape these objects take.
 */
export function isChatRuntime(pipeline) {
  if (!pipeline) return false;
  const runtime = pipeline.runtime || pipeline.marketplace?.runtime;
  if (runtime === 'chat') return true;
  const slug =
    pipeline.marketplace?.slug || pipeline.marketplaceSlug || pipeline.slug;
  return !!slug && CHAT_RUNTIME_SLUGS.has(slug);
}

/** User-facing copy for the one-Copilot-per-account cap. Matches the backend
 *  COPILOT_LIMIT_MESSAGE so the disabled-button hint and the API rejection read
 *  identically. */
export const COPILOT_LIMIT_MESSAGE =
  'Zibby supports one Copilot per account at this time.';
